/**
 * Moya main page — links each table-of-contents entry (from the page's
 * custom.toc metafield) to a best-effort matching heading/list item found in
 * the rendered page content. Entries with no match render as plain text
 * instead of a broken anchor link.
 */

class TableOfContents extends HTMLElement {
	connectedCallback() {
		this.renormalizeEntries();

		const content = document.querySelector(this.getAttribute("content-selector"));
		const links = Array.from(this.querySelectorAll("[data-toc-label]"));

		if (!content || links.length === 0) {
			return;
		}

		const candidates = Array.from(content.querySelectorAll("h2, h3, h4, li"));
		const usedSlugs = new Set();

		links.forEach((link) => {
			const label = link.dataset.tocLabel.trim().toLowerCase();
			const match = candidates.find((candidate) => {
				const text = this.extractLabel(candidate).toLowerCase();
				return text === label || text.startsWith(label);
			});

			if (!match) {
				link.removeAttribute("href");
				link.setAttribute("aria-disabled", "true");
				return;
			}

			if (!match.id) {
				match.id = this.slugify(this.extractLabel(match), usedSlugs);
			}

			link.href = `#${match.id}`;
		});
	}

	// The `custom.toc` metafield is meant to be one entry per line, and
	// sections/main-page.liquid splits it on "\n" server-side. Some line
	// breaks entered in the Shopify admin's metafield editor aren't a plain
	// "\n" though (e.g. "\r\n", or U+2028 LINE SEPARATOR from a rich-text
	// widget) — Liquid's split misses those and the whole field lands in a
	// single <li>. Rejoin whatever <li> Liquid produced and re-split on
	// every line-break variant, then rebuild the list — a no-op if Liquid's
	// split already worked, a fix if it didn't.
	renormalizeEntries() {
		const list = this.querySelector("ul");
		const items = list ? Array.from(list.querySelectorAll("[data-toc-label]")) : [];

		if (items.length === 0) {
			return;
		}

		const combined = items.map((item) => item.dataset.tocLabel).join("\n");
		const labels = combined
			.split(/\r\n|\r|\n|\u2028|\u2029/)
			.map((label) => label.trim())
			.filter(Boolean);

		if (labels.length === items.length) {
			return;
		}

		list.innerHTML = "";

		labels.forEach((label) => {
			const li = document.createElement("li");
			const a = document.createElement("a");
			a.dataset.tocLabel = label;
			a.textContent = label;
			li.appendChild(a);
			list.appendChild(li);
		});
	}

	// Some content (e.g. imports from other platforms) marks section titles as
	// list items whose own text is followed by a nested <ol>/<ul> of
	// sub-points. Strip nested lists before reading textContent so matching
	// isn't thrown off by the sub-list's text.
	extractLabel(element) {
		const clone = element.cloneNode(true);
		clone.querySelectorAll("ol, ul").forEach((nested) => nested.remove());
		return clone.textContent.replace(/\s+/g, " ").trim();
	}

	slugify(text, usedSlugs) {
		let base = text
			.trim()
			.toLowerCase()
			.replace(/ł/g, "l")
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "");

		if (!base) {
			base = "section";
		}

		let slug = base;
		let counter = 1;

		while (usedSlugs.has(slug)) {
			slug = `${base}-${counter}`;
			counter += 1;
		}

		usedSlugs.add(slug);
		return slug;
	}
}

if (!window.customElements.get("table-of-contents")) {
	window.customElements.define("table-of-contents", TableOfContents);
}

/**
 * Product gallery sliders (.blockSlidersGallery__product__slider) — small
 * per-product image sliders originally powered by Splide.js, whose dot
 * pagination it generates dynamically. That library isn't loaded on this
 * theme; the CSS turns .splide__list into a plain scroll-snap slider, and
 * this adds the missing dots and keeps them in sync with scroll position.
 */
function initGallerySliderDots() {
	const sliders = document.querySelectorAll(".blockSlidersGallery__product__slider");

	sliders.forEach((slider) => {
		if (slider.querySelector(".blockSlidersGallery__dots")) {
			return;
		}

		const list = slider.querySelector(".splide__list");
		const slides = list ? Array.from(list.querySelectorAll(".splide__slide")) : [];

		if (!list || slides.length < 2) {
			return;
		}

		const dots = document.createElement("div");
		dots.className = "blockSlidersGallery__dots";

		slides.forEach((slide, index) => {
			const dot = document.createElement("button");
			dot.type = "button";
			dot.className = "blockSlidersGallery__dot";
			dot.setAttribute("aria-label", `Zdjęcie ${index + 1} z ${slides.length}`);
			dot.classList.toggle("is-active", index === 0);
			dot.addEventListener("click", () => {
				slide.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
			});
			dots.appendChild(dot);
		});

		slider.appendChild(dots);

		const dotElements = Array.from(dots.children);
		let frame = null;

		list.addEventListener(
			"scroll",
			() => {
				if (frame) {
					return;
				}

				frame = requestAnimationFrame(() => {
					frame = null;

					const activeIndex = slides.reduce((closest, slide, index) => {
						const closestDistance = Math.abs(slides[closest].offsetLeft - list.scrollLeft);
						const distance = Math.abs(slide.offsetLeft - list.scrollLeft);
						return distance < closestDistance ? index : closest;
					}, 0);

					dotElements.forEach((dot, index) => {
						dot.classList.toggle("is-active", index === activeIndex);
					});
				});
			},
			{ passive: true },
		);
	});
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initGallerySliderDots);
} else {
	initGallerySliderDots();
}

/**
 * WordPress "wldevs-block-collapse" accordion blocks (e.g. "Tea expert" /
 * "Zobacz ofertę" on the careers page) — migrated markup with no JS behind
 * it on Shopify. Clicking the title toggles the `is-open` class, which
 * moya-main-page.css uses to show the content and swap the +/- icon.
 */
function initCollapseBlocks() {
	const collapses = document.querySelectorAll(".wp-block-wldevs-block-collapse");

	collapses.forEach((collapse) => {
		const title = collapse.querySelector(".wp-block-wldevs-block-collapse__title");
		const content = collapse.querySelector(".wp-block-wldevs-block-collapse__content");

		if (!title || !content || title.dataset.collapseBound) {
			return;
		}

		title.dataset.collapseBound = "true";
		title.setAttribute("role", "button");
		title.setAttribute("tabindex", "0");
		title.setAttribute("aria-expanded", "false");

		if (!content.id) {
			content.id = `wldevs-collapse-${Math.random().toString(36).slice(2, 9)}`;
		}

		title.setAttribute("aria-controls", content.id);

		const toggle = () => {
			const isOpen = collapse.classList.toggle("is-open");
			title.setAttribute("aria-expanded", String(isOpen));
		};

		title.addEventListener("click", toggle);
		title.addEventListener("keydown", (event) => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				toggle();
			}
		});
	});
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", initCollapseBlocks);
} else {
	initCollapseBlocks();
}

/**
 * Wraps every plain <table> pasted into CMS page content with
 * .main-page__table-wrapper. border-radius has no effect on a <table>
 * using border-collapse: collapse (a CSS spec exception, not a browser
 * bug), so the rounded corners/outer border have to live on a wrapper
 * element instead of the table itself — see moya-main-page.css.
 */
function wrapContentTables() {
	const tables = document.querySelectorAll(".main-page__content table");

	tables.forEach((table) => {
		if (table.closest(".main-page__table-wrapper")) {
			return;
		}

		const wrapper = document.createElement("div");
		wrapper.className = "main-page__table-wrapper";
		table.replaceWith(wrapper);
		wrapper.appendChild(table);
	});
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", wrapContentTables);
} else {
	wrapContentTables();
}

